import { useState } from 'react'

import { TaggableUser } from '@api'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import { useToast } from '@/components/ui/toast'

import { buildTaggableCreatorsInvitationUrl } from '../TagOtherCreatorsAndReleaseFormsDialog/utils'
import type { DialogProps } from '../types'
import { useInvitationMutations } from './useInvitationMutations'
import type { FormValues } from './validation'
import { FORM_ID, resolver } from './validation'

interface Props extends DialogProps {
  tag?: Pick<TaggableUser, 'name' | 'id' | 'token'>
  onEdit?: (id: number, name: string) => void
}

export const InviteTaggableCreatorDialog = ({
  isOpen,
  onClose,
  onEdit: onEditProp,
  tag,
}: Props) => {
  const [inviteUrl, setInviteUrl] = useState<string | null>(
    tag?.token ? buildTaggableCreatorsInvitationUrl(tag.token) : null,
  )
  const form = useForm<FormValues>({
    defaultValues: { name: tag?.name ?? '' },
    resolver,
  })
  const { onEdit, onCreate } = useInvitationMutations()
  const toast = useToast()

  const handleEdit = async (
    id: TaggableUser['id'],
    name: TaggableUser['name'],
  ) => {
    await onEdit({ name, id })
    onEditProp?.(id, name)
    onClose()
  }

  const handleCreate = async (name: TaggableUser['name']) => {
    const invitation = await onCreate(name)

    setInviteUrl(invitation.url ?? null)
  }

  const onSubmit = async ({ name }: FormValues) => {
    if (tag) {
      await handleEdit(tag.id, name)
    } else {
      await handleCreate(name)
    }
  }

  const handleClickCopy = () => {
    if (!inviteUrl) return

    navigator.clipboard.writeText(inviteUrl)
    toast.success('Link copied to clipboard')
    onClose()
  }

  const isEditMode = !!tag
  const isCreateMode = !inviteUrl && !isEditMode
  const isCreated = !!inviteUrl && !isEditMode

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[533px]"
      showCloseIcon={false}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Invite New User</Dialog.Title>
          <Dialog.Description className="text-center">
            Invite a user who does not have an account yet
          </Dialog.Description>
        </Dialog.Header>
        <FormProvider {...form}>
          <form
            id={FORM_ID}
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-3"
          >
            {(isEditMode || isCreateMode) && (
              <FormField
                label="Name"
                name="name"
              />
            )}
            {isCreateMode && (
              <button
                className="text-primary-500 underline"
                onClick={onClose}
                type="button"
              >
                Looking for someone who has an account already?
              </button>
            )}
            {(isCreated || isEditMode) && (
              <div className="flex flex-col gap-1">
                <div>
                  Please provide this link to the Creator featured in your
                  content.
                </div>
                <button
                  type="button"
                  className="text-primary-500 gap-1 text-left hover:underline"
                  onClick={handleClickCopy}
                >
                  {inviteUrl}
                </button>
                <div className="text-secondary-300 text-xs">
                  Please note that the link is valid for 21 days. The
                  verification takes a few minutes to complete.
                </div>
              </div>
            )}
          </form>
        </FormProvider>
        <Dialog.ClosableFooter onClose={onClose}>
          {isCreateMode && (
            <Button
              type="submit"
              form={FORM_ID}
              isLoading={form.formState.isSubmitting}
              disabled={!form.formState.isValid || !form.formState.isDirty}
            >
              Invite
            </Button>
          )}
          {isEditMode && (
            <Button
              type="submit"
              form={FORM_ID}
              isLoading={form.formState.isSubmitting}
              disabled={!form.formState.isValid || !form.formState.isDirty}
            >
              Save
            </Button>
          )}
          {isCreated && <Button onClick={handleClickCopy}>Copy Link</Button>}
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
